// Hideo - Italian translations: pages (home, vault, keywords, creators, sources, help).
// Keys are the English source strings used in the code (see docs/i18n.md).
import type { Dictionary } from '../types'

const it: Dictionary = {
    // ===== Stored values shown with tv() (creator types, verdicts, keyword status). tv() maps an Italian value back to
    // the first English key that translates to it, in the merge order of src/i18n/index.ts (core.ts first): an entry
    // here that repeats one of core.ts never decides it.
    'Collaborator': 'Collaboratore',
    'Competitor': 'Competitor',
    'to use': 'da usare',
    'used': 'usata',

    // ===== Home: "Drop your idea here" =====
    'Voice': 'Vocale',
    'Write your idea': 'Scrivi la tua idea',
    'Title (optional)': 'Titolo (facoltativo)',
    'Record a voice note': 'Registra un vocale',
    'Remove the voice note': 'Togli il vocale',
    'Send to the plan': 'Manda al PED',
    'Play': 'Ascolta',
    'Pause': 'Pausa',
    'Position in the voice note': 'Punto del vocale',
    'Recording…': 'Registrazione in corso…',
    'Stop': 'Ferma',
    'File': 'File',
    'Choose an audio file': 'Scegli un file audio',
    'Up to {n} minutes. The longer the voice note, the bigger the file on Google Drive.': 'Massimo {n} minuti. Più il vocale è lungo, più pesa il file su Google Drive.',
    'Remove the image': "Togli l'immagine",
    'Take or choose a picture': "Scatta o scegli un'immagine",
    'Take the image from a link': "Prendi l'immagine da un link",
    'Microphone not available: check that you gave the site permission to use the microphone.': 'Microfono non disponibile: controlla di aver dato al sito il permesso di usare il microfono.',
    'Audio file too large (3 MB max): record a shorter voice note.': 'File audio troppo grande (massimo 3 MB): registra un vocale più corto.',
    'Here you can add a text, an audio file or an image.': "Qui puoi aggiungere un testo, un audio o un'immagine.",
    'Idea added to the plan': 'Idea aggiunta al PED',
    'Added to the plan: "{title}"': 'Aggiunto al PED: "{title}"',

    // ===== Home: welcome, project, upcoming releases, help =====
    'Welcome,': 'Benvenuto,',
    'Previous': 'Indietro',
    'Page {n}': 'Pagina {n}',
    'Need a hand?': 'Hai bisogno di una mano?',
    'A quick tour of Hideo, in less than a minute.': 'Un giro veloce di Hideo, in meno di un minuto.',
    'Or read the guide': 'Oppure leggi la guida',
    'See the tutorial': 'Guarda il tutorial',
    'Changes not saved yet': 'Modifiche non ancora salvate',
    'Profiles, saves and Google Drive': 'Profili, salvataggi e Google Drive',
    'Search': 'Cerca',
    '{n} collaborator': '{n} collaboratore',
    '{n} collaborators': '{n} collaboratori',
    '{n} competitor': '{n} competitor',
    '{n} competitors': '{n} competitor',
    'Upcoming releases': 'Prossime uscite',
    'at {time}': 'ore {time}',
    'No videos scheduled. Give a video an end of work (publication day) from the Plan, the Calendar or the Timeline.': 'Nessun video in programma. Dai una fine lavoro (giorno di pubblicazione) a un video dal PED, dal Calendario o dalla Timeline.',

    // ===== Hooks =====
    'No hooks yet.': 'Ancora nessun hook.',
    'No hooks found.': 'Nessun hook trovato.',
    'Add the first hook': 'Aggiungi il primo hook',
    'Open the Calendar': 'Apri il Calendario',
    'Search hooks…': 'Cerca negli hook…',
    'Edit hook': 'Modifica Hook',
    'New hook': 'Nuovo Hook',
    'Enter at least a title for the hook!': "Inserisci almeno un titolo per l'hook!",
    'Hook added': 'Hook aggiunto',
    'Are you sure you want to delete this hook?': 'Sei sicuro di voler eliminare questo hook?',
    // Hook dialog (HookDialog.vue)
    'E.g. Hook for a Short about Nintendo': 'Es. Hook per Short su Nintendo',
    'CONTENT / HOOK': 'CONTENUTO / GANCIO',
    'Write your idea, hook or story hook here... (@ to mention)': 'Scrivi qui il tuo spunto, hook o gancio narrativo... (@ per menzionare)',
    'Delete hook': 'Elimina hook',
    'Save hook': 'Salva hook',

    // ===== Keywords =====
    'No suggestions: try simpler words.': 'Nessun suggerimento: prova con parole più semplici.',
    "I can't reach YouTube: are you connected to the internet?": 'Non riesco a contattare YouTube: sei connesso a internet?',
    'What people search for on YouTube': 'Cosa cerca la gente su YouTube',
    'Type a topic, e.g. super mario 64': 'Scrivi un argomento, es. super mario 64',
    'Also with the letters from A to Z': 'Anche con le lettere dalla A alla Z',
    'More ideas (A–Z)': 'Più idee (A–Z)',
    'These are the suggestions YouTube shows while people type: real ideas, without search numbers (only paid services have those). To see the trend over time, add them to the Google Trends comparison below.': "Sono i suggerimenti che YouTube mostra mentre si scrive: idee reali, senza numeri di ricerche (quelli li hanno solo i servizi a pagamento). Per capire l'andamento nel tempo aggiungile al confronto di Google Trends qui sotto.",
    'See it on Google Trends': 'Vedilo su Google Trends',
    'In the Google Trends comparison': 'Nel confronto di Google Trends',
    'Add to the Google Trends comparison': 'Aggiungi al confronto di Google Trends',
    'Remove from the Google Trends comparison': 'Togli dal confronto di Google Trends',
    // src/components/TrendsPanel.vue
    'Searches over time (Google Trends)': 'Ricerche nel tempo (Google Trends)',
    'Open in Google Trends': 'Apri in Google Trends',
    'Compare on Google Trends': 'Confronta su Google Trends',
    'Open only this keyword in Google Trends, with its related searches': 'Apri solo questa parola in Google Trends, con le sue ricerche correlate',
    'Google Trends opens in a new tab with your keywords and options: there you see the searches over time and the related searches. Tap a keyword to open it alone. Google does not let its charts be shown inside other apps.': 'Google Trends si apre in una nuova scheda con le tue parole e le tue scelte: lì vedi le ricerche nel tempo e le ricerche correlate. Tocca una parola per aprirla da sola. Google non permette di mostrare i suoi grafici dentro altre app.',
    'Add up to {n} keywords to see on Google Trends how much they are searched over time and compare them. You can also add them from the search above or from your bank, with the chart button.': 'Aggiungi fino a {n} parole per vedere su Google Trends quanto vengono cercate nel tempo e confrontarle. Puoi aggiungerle anche dalla ricerca qui sopra o dalla tua banca, con il pulsante del grafico.',
    'Add a keyword to compare (up to {n})': 'Aggiungi una parola da confrontare (fino a {n})',
    'Up to {n} keywords: remove one to add another': "Al massimo {n} parole: togline una per aggiungerne un'altra",
    'Compare': 'Confronta',
    'Remove from the comparison': 'Togli dal confronto',
    'Area': 'Zona',
    'Italy': 'Italia',
    'World': 'Mondo',
    'Period': 'Periodo',
    '7 days': '7 giorni',
    '30 days': '30 giorni',
    '12 months': '12 mesi',
    '5 years': '5 anni',
    'Searches on': 'Ricerche su',
    'Your keyword bank': 'La tua banca di parole chiave',
    'Add a keyword by hand': 'Aggiungi a mano una parola chiave',
    'See the results on YouTube': 'Vedi i risultati su YouTube',
    'Asking YouTube…': 'Chiedo a YouTube…',
    '{n} idea · tap {icon} to save it to the bank': '{n} idea · tocca {icon} per salvarla nella banca',
    '{n} ideas · tap {icon} to save them to the bank': '{n} idee · tocca {icon} per salvarle nella banca',
    'Already in the bank': 'Già nella banca',
    'Save to the bank': 'Salva nella banca',
    'All keywords': 'Tutte',
    'To use': 'Da usare',
    'Used': 'Usate',
    'Used in {n} video: {titles}': 'Usata in {n} video: {titles}',
    'Used in {n} videos: {titles}': 'Usata in {n} video: {titles}',
    'Marked as used': 'Segnata come usata',
    'Idea in the plan': 'Idea nel PED',
    'Mark as to use': 'Segna da usare',
    'Mark as used': 'Segna come usata',
    'Search on YouTube': 'Cerca su YouTube',
    'No keywords in this filter.': 'Nessuna parola chiave in questo filtro.',
    'The bank is empty: look for some ideas above and save the good ones.': 'La banca è vuota: cerca qualche idea qui sopra e salva quelle buone.',
    'Remove "{text}" from the bank?': 'Togliere "{text}" dalla banca?',
    'Idea "{title}" added to the plan, Idea column.': 'Idea "{title}" aggiunta al PED, colonna Idea.',
    'Hook "{title}" created.': 'Hook "{title}" creato.',

    // ===== Creators =====
    'Collaborators': 'Collaboratori',
    'Competitors': 'Competitor',
    'None yet': 'Nessuno ancora',
    'Tap to add some': 'Tocca per aggiungerne',
    'Search competitors…': 'Cerca tra i competitor…',
    'Search collaborators…': 'Cerca tra i collaboratori…',
    'Tap <b>Collaborators</b> or <b>Competitors</b> to see the list.': "Tocca <b>Collaboratori</b> o <b>Competitor</b> per vedere l'elenco.",
    'No results for "{q}".': 'Nessun risultato per "{q}".',
    'No competitors yet.': 'Nessun competitor ancora.',
    'No collaborators yet.': 'Nessun collaboratore ancora.',
    'Add the first one': 'Aggiungi il primo',
    '{n} analysed video': '{n} video analizzato',
    '{n} analysed videos': '{n} video analizzati',
    'No analysed videos': 'Nessun video analizzato',
    'also competitor': 'anche competitor',
    'also collaborator': 'anche collaboratore',
    'Edit creator': 'Modifica Profilo',
    "Enter the creator's name!": 'Inserisci il nome del Creator!',
    'Banner': 'Banner',
    'A wide image shown behind the photo, like a channel banner': 'Un\'immagine larga dietro la foto, come il banner di un canale',
    'Change banner': 'Cambia banner',
    'Remove banner': 'Rimuovi banner',
    'Are you sure you want to delete this creator?': 'Sei sicuro di voler eliminare questo creator?',
    'Open video': 'Apri video',
    'Edit analysed video': 'Modifica Video Analizzato',
    'New analysed video': 'Nuovo Video Analizzato',
    'Enter the title of the analysed video!': 'Inserisci il titolo del video analizzato!',
    'Delete this analysed video?': 'Eliminare questo video analizzato?',
    'Delete analysed video': 'Elimina video analizzato',
    'Creator card': 'Scheda creator',
    'Tap to add a bio, strengths/weaknesses and a rating.': 'Tocca per aggiungere bio, punti di forza/debolezza e valutazione.',
    "Edit Profile": "Modifica Profilo",
    "Analysed Videos": "Video Analizzati",
    "Add Analysed Video": "Aggiungi Video Analizzato",
    'Videos to analyse': 'Video da analizzare',
    '{n} video to analyse': '{n} video da analizzare',
    '{n} videos to analyse': '{n} video da analizzare',
    'Title or note (optional)': 'Titolo o nota (facoltativo)',
    'Analyse this video': 'Analizza questo video',
    'Analyse': 'Analizza',
    'Paste the link of a video you want to analyse later.': 'Incolla il link di un video che vuoi analizzare più avanti.',
    "E.g. Slends": "Es. Slends",
    "TYPE": "TIPO",
    "(one or both)": "(uno o entrambi)",
    "CREATOR BIO": "BIO DEL CREATOR",
    "Who they are, what they talk about, what their channel is like... (@ to mention)": "Chi è, di cosa parla, com'è il suo canale... (@ per menzionare)",
    "STRENGTHS": "PUNTI DI FORZA",
    "WEAKNESSES": "PUNTI DI DEBOLEZZA",
    "RATING": "VALUTAZIONE",
    "VIDEO RATING": "VALUTAZIONE VIDEO",
    "To evaluate": "Da valutare",
    "YOUR NOTES": "NOTE",
    "Style, niche, remarks... (@ to mention)": "Stile, nicchia, osservazioni... (@ per menzionare)",
    "Delete creator": "Elimina creator",
    "Add thumbnail": "Aggiungi miniatura",
    "(if empty and the link is a YouTube one, I fetch it automatically)": "(se vuoto e il link è di YouTube, la prendo in automatico)",
    "VIDEO TITLE": "TITOLO VIDEO",
    "E.g. How to make money in 2026": "Es. Come guadagnare nel 2026",
    "LINK TO THE REFERENCE VIDEO": "LINK AL VIDEO DI RIFERIMENTO",
    "ANALYSIS / NOTES": "ANALISI / APPUNTI",
    "Notes, structure, pacing, hook, summary of the video... (@ to mention)": "Appunti, struttura, ritmo, hook, sintesi del video... (@ per menzionare)",
    "What worked especially well...": "Cosa ha funzionato particolarmente bene...",
    "What could be improved...": "Cosa si potrebbe migliorare...",

    // ===== Sources =====
    'All sources': 'Tutte le fonti',
    'No tag': 'Senza tag',
    'No sources yet': 'Nessuna fonte ancora',
    'New tag': 'Nuovo tag',
    'Add tag': 'Aggiungi tag',
    'Search all sources…': 'Cerca in tutte le fonti…',
    'Search in "{tag}"…': 'Cerca in "{tag}"…',
    'No sources here yet.': 'Nessuna fonte qui ancora.',
    'Add the first source': 'Aggiungi la prima',
    'No link': 'Nessun link',
    'Open the site': 'Apri il sito',
    'Edit source': 'Modifica Fonte',
    'New source': 'Nuova Fonte',
    'Enter at least a title for the source!': 'Inserisci almeno un titolo per la fonte!',
    'Are you sure you want to delete this source?': 'Sei sicuro di voler eliminare questa fonte?',
    // Source dialog (SourceDialog.vue; "(one or more)" also in the hook dialog)
    '(one or more)': '(uno o più)',
    'Change thumbnail': 'Cambia miniatura',
    'E.g. YouTube Trends report': 'Es. Report YouTube Trends',
    'SOURCE SITE URL': 'URL SITO SORGENTE',
    'FACT-CHECKING NOTES': 'NOTE DI FACT-CHECKING',
    'Reliability, verified data, remarks... (@ to mention)': 'Affidabilità, dati verificati, osservazioni... (@ per menzionare)',
    'Delete source': 'Elimina fonte',
    'Save source': 'Salva fonte',

    // ===== Saved videos =====
    'Saved videos': 'Video salvati',
    'Saved video': 'Video salvato',
    'FORMAT': 'FORMAT',
    'Reels and videos found on the socials, to watch again later': 'Reel e video trovati sui social, da ripescare più tardi',
    '{platform} video': 'Video {platform}',
    'All videos': 'Tutti i video',
    'No videos yet': 'Nessun video ancora',
    'Save a video': 'Salva un video',
    'Paste the link of a reel, a short, a TikTok…': 'Incolla il link di un reel, uno short, un TikTok…',
    'Search all saved videos…': 'Cerca in tutti i video salvati…',
    'Tap a tag to see its videos.': 'Tocca un tag per vedere i suoi video.',
    'PLATFORMS': 'PIATTAFORME',
    'Search the videos without tags…': 'Cerca nei video senza tag…',
    'Found a reel you want to watch again later? Copy its link and add it with <b>Save a video</b>, or drop the link on this page.': 'Hai trovato un reel da riguardare più tardi? Copia il link e aggiungilo con <b>Salva un video</b>, oppure trascina il link su questa pagina.',
    'Watch the video': 'Guarda il video',
    'No saved videos yet.': 'Nessun video salvato ancora.',
    'Save the first video': 'Salva il primo video',
    'Edit saved video': 'Modifica video salvato',
    'VIDEO LINK': 'LINK DEL VIDEO',
    '(not required)': '(non obbligatorio)',
    'E.g. Great hook in the first 3 seconds': 'Es. Hook fortissimo nei primi 3 secondi',
    "Why you saved it, what to copy… (@ to mention)": "Perché l'hai salvato, cosa riprendere… (@ per menzionare)",
    'Paste the link of the video!': 'Incolla il link del video!',
    'Are you sure you want to delete this saved video?': 'Sei sicuro di voler eliminare questo video salvato?',
    'Delete video': 'Elimina video',
    'Delete saved video': 'Elimina video salvato',
    '{n} saved video': '{n} video salvato',
    '{n} saved videos': '{n} video salvati',
    // src/pages/SourcesPage.vue, src/components/dialogs/SourceDialog.vue, SavedVideoDialog.vue, SavedPage.vue (categories)
    'Tap a category to see its sources.': 'Tocca una categoria per vedere le sue fonti.',
    'Add a source with <b>New source</b>: choose what kind it is (article, scientific paper, TV…) and give it the tags of its themes.': 'Aggiungi una fonte con <b>Nuova fonte</b>: scegli che tipo è (articolo, paper scientifico, TV…) e dalle i tag dei suoi temi.',
    'An article, a paper, a TV programme…': 'Un articolo, un paper, un programma TV…',
    'Search sources without a category…': 'Cerca nelle fonti senza categoria…',
    'No category': 'Senza categoria',
    'CATEGORY': 'CATEGORIA',
    'CATEGORIES': 'CATEGORIE',
    '(what kind of source it is)': '(che tipo di fonte è)',
    '(what kind of video it is)': '(che tipo di video è)',
    '(the themes, one or more)': '(i temi, uno o più)',
    'Manage categories': 'Gestisci categorie',
    // src/features/categories.ts: the fixed categories
    'Scientific paper': 'Paper scientifico',
    'Book': 'Libro',
    'TV': 'TV',
    'Podcast': 'Podcast',
    'Website': 'Sito web',
    'Social post': 'Post social',
    'Official document': 'Documento ufficiale',
    'Data and statistics': 'Dati e statistiche',
    'Interview': 'Intervista',
    'Tutorial': 'Tutorial',
    'Review': 'Recensione',
    'Vlog': 'Vlog',
    'Reaction': 'Reaction',
    'Trend or meme': 'Trend o meme',
    'Storytelling': 'Storytelling',
    'Hook to study': 'Hook da studiare',
    'Editing to study': 'Montaggio da studiare',
    'Thumbnail to study': 'Miniatura da studiare',
    // src/components/dialogs/CategoryDialog.vue
    'Categories of sources': 'Categorie delle fonti',
    'Categories of saved videos': 'Categorie dei video salvati',
    'FIXED CATEGORIES': 'CATEGORIE FISSE',
    'They are the same for everyone and never change. Use tags for the themes (e.g. Nintendo, Football).': 'Sono uguali per tutti e non cambiano mai. Per i temi usa i tag (per esempio Nintendo, Calcio).',
    'YOUR CATEGORIES': 'LE TUE CATEGORIE',
    'No categories of your own yet.': 'Non hai ancora categorie tue.',
    'New category, e.g. Comics': 'Nuova categoria, per esempio Fumetti',
    'Change icon': "Cambia l'icona",
    'Choose an icon': "Scegli un'icona",
    'There is already a category with this name.': "C'è già una categoria con questo nome.",
    'Delete the category "{name}"? {n} item will stay, without a category.': 'Eliminare la categoria "{name}"? {n} elemento resterà, senza categoria.',
    'Delete the category "{name}"? {n} items will stay, without a category.': 'Eliminare la categoria "{name}"? {n} elementi resteranno, senza categoria.',
    // src/components/video/VideoRefs.vue, RefUsage.vue, src/components/dialogs/RefsPickDialog.vue
    'SOURCES AND REFERENCES': 'FONTI E RIFERIMENTI',
    'Link sources and saved videos': 'Collega fonti e video salvati',
    'Paste a link to add it here': 'Incolla un link per aggiungerlo qui',
    'As a source': 'Come fonte',
    'As a saved video': 'Come video salvato',
    'Unlink (it stays in its page)': 'Scollega (resta nella sua pagina)',
    'USED IN': 'USATO IN',
    'Search sources…': 'Cerca fonti…',
    'Search saved videos…': 'Cerca video salvati…',
    'No sources in this project yet.': 'Ancora nessuna fonte in questo progetto.',
    'No saved videos in this project yet.': 'Ancora nessun video salvato in questo progetto.',
    'Done': 'Fatto',
    // src/pages/FormatsPage.vue, src/components/dialogs/FormatDialog.vue, src/features/formatSpec.ts
    'Your recurring shows and series, each with its card: what it is and how it is made': 'Le tue rubriche e serie che tornano, ognuna con la sua scheda: cos\'è e come si fa',
    'Search formats…': 'Cerca format…',
    'Name of the new format (e.g. Trivia, Super Mario 64):': 'Nome del nuovo format (per esempio Trivia, Super Mario 64):',
    'This project already has a format with this name: here is its card.': 'Questo progetto ha già un format con questo nome: ecco la sua scheda.',
    'This project already has a format with this name.': 'Questo progetto ha già un format con questo nome.',
    'Tap to fill in its card': 'Tocca per compilare la sua scheda',
    '{n} ready or out': '{n} pronti o usciti',
    'Next: {date}': 'Prossimo: {date}',
    'A format is a show that comes back, like a weekly trivia or a series about one game. Create one and write its card: audience, structure, fixed rules and the details of its content type.': 'Un format è una rubrica che torna, come un trivia settimanale o una serie su un gioco. Creane uno e scrivi la sua scheda: pubblico, struttura, regole fisse e i dettagli del suo tipo di contenuto.',
    'Create the first format': 'Crea il primo format',
    'Format card': 'Scheda del format',
    'Open the format card': 'Apri la scheda del format',
    'WHAT IT IS': "COS'È",
    'In a few words: the idea of the show, why people watch it (@ to mention)': 'In poche parole: l\'idea della rubrica, perché la gente la guarda (@ per menzionare)',
    'Base information': 'Informazioni di base',
    'CONTENT TYPES': 'TIPI DI CONTENUTO',
    'You can choose more than one. Below, the details that each type needs.': 'Puoi sceglierne più di uno. Qui sotto, i dettagli che servono a ogni tipo.',
    'Choose one or more to see the details that each type of content needs.': 'Scegline uno o più per vedere i dettagli che servono a ogni tipo di contenuto.',
    'Reference audio': 'Audio di riferimento',
    '{n} song or sound saved': '{n} canzone o audio salvato',
    '{n} songs or sounds saved': '{n} canzoni o audio salvati',
    'Songs and sounds to use in this format': 'Canzoni e audio da usare in questo format',
    'Open the Audio view': 'Apri la vista Audio',
    'Cards': 'Schede',
    'Audio': 'Audio',
    'The songs and sounds to use in each format: trends, theme songs, background music. Save them with a link to find them again.': 'Le canzoni e gli audio da usare in ogni format: trend, sigle, musiche di sottofondo. Salvali con un link per ritrovarli.',
    'Song or sound': 'Canzone o audio',
    'Song or sound, e.g. the trending audio of the week': "Canzone o audio, per esempio l'audio di tendenza della settimana",
    'Link (optional)': 'Link (facoltativo)',
    'Remove "{name}" from the songs of this format?': 'Togliere "{name}" dalle canzoni di questo format?',
    'No songs or sounds saved yet.': 'Ancora nessuna canzone o audio salvato.',
    '{type} details': 'Dettagli {type}',
    'Videos of this format': 'Video di questo format',
    'New video in this format': 'Nuovo video in questo format',
    'No videos in this format yet.': 'Ancora nessun video in questo format.',
    'This format no longer exists.': 'Questo format non esiste più.',
    'Give the format a name!': 'Dai un nome al format!',
    'Delete the format "{name}"? Its videos stay, without a format.': 'Eliminare il format "{name}"? I suoi video restano, senza format.',
    'Delete the format "{name}"?': 'Eliminare il format "{name}"?',
    'Delete format': 'Elimina format',
    'Save card': 'Salva scheda',
    'Audience': 'Pubblico',
    'Who it is for, e.g. Nintendo fans aged 18–30': 'A chi è rivolto, per esempio fan Nintendo tra 18 e 30 anni',
    'Goal': 'Obiettivo',
    'E.g. grow the channel, keep the community active': 'Per esempio far crescere il canale, tenere viva la community',
    'How often': 'Ogni quanto',
    'Every week': 'Ogni settimana',
    'Every two weeks': 'Ogni due settimane',
    'Every month': 'Ogni mese',
    'When it fits': 'Quando capita',
    'Release day and time': 'Giorno e ora di uscita',
    'E.g. Friday at 6 pm': 'Per esempio venerdì alle 18',
    'The parts of every episode, in order (e.g. hook, intro, three questions, outro)': 'Le parti di ogni puntata, in ordine (per esempio hook, intro, tre domande, saluti)',
    'Fixed rules': 'Regole fisse',
    'What never changes: recurring lines, theme song, graphics, things to avoid…': 'Quello che non cambia mai: frasi ricorrenti, sigla, grafiche, cose da evitare…',
    'Fixed hashtags': 'Hashtag fissi',
    'Usual length': 'Durata tipica',
    'E.g. 12–15 minutes': 'Per esempio 12–15 minuti',
    'Intro and theme song': 'Intro e sigla',
    'E.g. 5-second animated logo after the hook': 'Per esempio logo animato di 5 secondi dopo l\'hook',
    'Editing style': 'Stile di montaggio',
    'Pace, cuts, music, subtitles, b-roll…': 'Ritmo, tagli, musica, sottotitoli, b-roll…',
    'Thumbnail style': 'Stile della miniatura',
    'Colours, face, big text, fixed template…': 'Colori, volto, testo grande, modello fisso…',
    '15 seconds': '15 secondi',
    '30 seconds': '30 secondi',
    '60 seconds': '60 secondi',
    '90 seconds': '90 secondi',
    '3 minutes': '3 minuti',
    'Hook of the first seconds': 'Hook dei primi secondi',
    'How every short opens, e.g. a question on screen': 'Come si apre ogni short, per esempio una domanda a schermo',
    'Looping ending': 'Finale in loop',
    'Kind of post': 'Tipo di post',
    'Carousel': 'Carosello',
    'Single photo': 'Foto singola',
    'Graphic': 'Grafica',
    'Text only': 'Solo testo',
    'Number of slides': 'Numero di slide',
    'E.g. 7: cover, 5 tips, call to action': "Per esempio 7: copertina, 5 consigli, invito all'azione",
    'Visual style': 'Stile grafico',
    'Colours, fonts, template, ratio (e.g. 4:5)': 'Colori, caratteri, modello, formato (per esempio 4:5)',
    'Caption template': 'Modello di didascalia',
    'How the caption is written, every time': 'Come si scrive la didascalia, ogni volta',
    'Where it is streamed': 'Dove va in onda',
    'E.g. Twitch and YouTube': 'Per esempio Twitch e YouTube',
    'E.g. 2 hours': 'Per esempio 2 ore',
    'Run sheet': 'Scaletta',
    'Start, segments, breaks, ending': 'Inizio, blocchi, pause, chiusura',
    'Chat interaction': 'Interazione con la chat',
    'Q&A, polls, challenges, viewer games…': 'Domande e risposte, sondaggi, sfide, giochi con il pubblico…',
    'Guests': 'Ospiti',
    'Who joins, how often': 'Chi partecipa, ogni quanto',
    'Moderation': 'Moderazione',
    'Moderators, chat rules': 'Moderatori, regole della chat',
    'Replay and clips': 'Replica e clip',
    'E.g. highlights cut into three shorts': 'Per esempio i momenti migliori tagliati in tre short',
    'Length (words)': 'Lunghezza (parole)',
    'E.g. 1200–1500': 'Per esempio 1200–1500',
    'Fixed sections': 'Sezioni fisse',
    'E.g. introduction, the news, my opinion, sources': 'Per esempio introduzione, la notizia, la mia opinione, fonti',
    'Fixed keywords': 'Parole chiave fisse',
    'Words that every article of this format uses': 'Parole che usa ogni articolo di questo format',
    'Newsletter': 'Newsletter',
    'E.g. sent on Sunday with a summary': 'Per esempio inviata la domenica con un riassunto',
}

export default it
